import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle2 } from 'lucide-react';
import { useLanguage, Language } from '../contexts/LanguageContext';

// Guía "No sé qué formulario necesito". Orientación general basada en los
// criterios publicados por travel.state.gov (DS-82 solo si cumple todas las
// condiciones; si no, DS-11). La decisión final la confirma Noor con el cliente.
// EN/ES finalizados. HT/VI = mejor esfuerzo, pendiente de revisión nativa.
// FormFinder se reutiliza en /gracias (después del pago) con renderActions.
type L = Record<Language, string>;
const l = (es: string, en: string, ht: string, vi: string): L => ({
  es,
  en,
  ht,
  vi
});
type Opt = {
  label: L;
  next: string;
  minor?: boolean;
  nextMinor?: string;
};
type Q = {
  q: L;
  opts: Opt[];
};
type R = {
  forms: string[];
  why: L;
};
const YES = l('Sí', 'Yes', 'Wi', 'Có');
const NO = l('No', 'No', 'Non', 'Không');
const yn = (yes: string, no: string): Opt[] => [{
  label: YES,
  next: yes
}, {
  label: NO,
  next: no
}];
const QUESTIONS: Record<string, Q> = {
  start: {
    q: l('¿Para quién es el pasaporte?', 'Who is the passport for?', 'Pou kiyès paspò a ye?', 'Hộ chiếu dành cho ai?'),
    opts: [{
      label: l('Para mí o para otro adulto (18 años o más)', 'For me or another adult (18 or older)', 'Pou mwen oswa pou yon lòt granmoun (18 an oswa plis)', 'Cho tôi hoặc người lớn khác (từ 18 tuổi)'),
      next: 'sit',
      minor: false
    }, {
      label: l('Para un menor de 18 años', 'For a child under 18', 'Pou yon timoun ki poko gen 18 an', 'Cho trẻ em dưới 18 tuổi'),
      next: 'sit',
      minor: true
    }]
  },
  sit: {
    q: l('¿Cuál es su situación?', 'What is your situation?', 'Ki sitiyasyon ou?', 'Tình huống của quý vị là gì?'),
    opts: [{
      label: l('Nunca ha tenido pasaporte de EE. UU.', 'Has never had a U.S. passport', 'Pa janm gen paspò ameriken', 'Chưa từng có hộ chiếu Hoa Kỳ'),
      next: 'R_first'
    }, {
      label: l('Quiero renovar (vencido o por vencer)', 'I want to renew (expired or expiring)', 'Mwen vle renouvle (ekspire oswa prèt pou ekspire)', 'Tôi muốn gia hạn (đã hết hạn hoặc sắp hết hạn)'),
      next: 'rn_hand',
      nextMinor: 'R_minor_renew'
    }, {
      label: l('Se perdió o me lo robaron', 'It was lost or stolen', 'Li pèdi oswa yo vòlè l', 'Bị mất hoặc bị đánh cắp'),
      next: 'lost_y'
    }, {
      label: l('Está dañado', 'It is damaged', 'Li domaje', 'Bị hư hỏng'),
      next: 'dmg_y'
    }, {
      label: l('Cambio de nombre o corrección de un error', 'Name change or correcting a mistake', 'Chanjman non oswa koreksyon yon erè', 'Đổi tên hoặc sửa lỗi'),
      next: 'name_q'
    }, {
      label: l('Lo solicité pero nunca lo recibí', 'I applied but never received it', 'Mwen fè demann nan men mwen pa janm resevwa l', 'Tôi đã nộp đơn nhưng chưa nhận được'),
      next: 'R_notrec'
    }, {
      label: l('Nada de esto / no estoy seguro', 'None of these / I am not sure', 'Okenn nan sa yo / mwen pa sèten', 'Không có mục nào / tôi không chắc'),
      next: 'R_unsure'
    }]
  },
  rn_hand: {
    q: l('¿Tiene en su poder el pasaporte anterior?', 'Do you have your previous passport in hand?', 'Eske ou gen ansyen paspò a nan men ou?', 'Quý vị có hộ chiếu cũ trong tay không?'),
    opts: yn('rn_dmg', 'lost_y')
  },
  rn_dmg: {
    q: l('¿Está en buen estado (solo desgaste normal)?', 'Is it in good condition (normal wear only)?', 'Eske li an bon eta (sèlman iz nòmal)?', 'Hộ chiếu còn tốt (chỉ hao mòn bình thường)?'),
    opts: yn('rn_15', 'dmg_y')
  },
  rn_15: {
    q: l('¿Se emitió hace menos de 15 años?', 'Was it issued less than 15 years ago?', 'Eske li te emèt sa gen mwens pase 15 an?', 'Hộ chiếu được cấp cách đây dưới 15 năm?'),
    opts: yn('rn_16', 'R_ds11_old')
  },
  rn_16: {
    q: l('¿Tenía 16 años o más cuando se lo emitieron?', 'Were you 16 or older when it was issued?', 'Eske ou te gen 16 an oswa plis lè yo te emèt li?', 'Quý vị từ 16 tuổi trở lên khi được cấp?'),
    opts: yn('rn_name', 'R_ds11_young')
  },
  rn_name: {
    q: l('¿Está a su nombre actual, o tiene el documento legal del cambio de nombre (acta de matrimonio, orden judicial)?', 'Is it in your current name, or do you have the legal document for the name change (marriage certificate, court order)?', 'Eske li nan non aktyèl ou, oswa ou gen dokiman legal chanjman non an (batèm maryaj, lòd tribinal)?', 'Hộ chiếu mang tên hiện tại của quý vị, hoặc quý vị có giấy tờ pháp lý đổi tên (giấy đăng ký kết hôn, lệnh tòa án)?'),
    opts: yn('R_ds82', 'R_ds11_name')
  },
  lost_y: {
    q: l('¿Ese pasaporte se emitió hace menos de 1 año?', 'Was that passport issued less than 1 year ago?', 'Eske paspò sa a te emèt sa gen mwens pase 1 an?', 'Hộ chiếu đó được cấp cách đây dưới 1 năm?'),
    opts: yn('R_lost_1y', 'R_lost')
  },
  dmg_y: {
    q: l('¿Ese pasaporte se emitió hace menos de 1 año?', 'Was that passport issued less than 1 year ago?', 'Eske paspò sa a te emèt sa gen mwens pase 1 an?', 'Hộ chiếu đó được cấp cách đây dưới 1 năm?'),
    opts: yn('R_dmg_1y', 'R_dmg')
  },
  name_q: {
    q: l('¿Qué ocurrió?', 'What happened?', 'Kisa ki pase?', 'Chuyện gì đã xảy ra?'),
    opts: [{
      label: l('El Departamento de Estado cometió un error de impresión en mi pasaporte', 'The State Department made a printing error on my passport', 'Depatman Deta a fè yon erè enprimri sou paspò mwen', 'Bộ Ngoại giao in sai trên hộ chiếu của tôi'),
      next: 'R_5504_err'
    }, {
      label: l('Cambié de nombre u otro dato (matrimonio, divorcio, orden judicial)', 'I changed my name or other data (marriage, divorce, court order)', 'Mwen chanje non mwen oswa lòt done (maryaj, divòs, lòd tribinal)', 'Tôi đã đổi tên hoặc thông tin khác (kết hôn, ly hôn, lệnh tòa án)'),
      next: 'name_1y'
    }]
  },
  name_1y: {
    q: l('¿Su pasaporte se emitió hace menos de 1 año?', 'Was your passport issued less than 1 year ago?', 'Eske paspò ou a te emèt sa gen mwens pase 1 an?', 'Hộ chiếu của quý vị được cấp cách đây dưới 1 năm?'),
    opts: yn('R_5504_chg', 'rn_hand')
  },
  consent: {
    q: l('¿Ambos padres o tutores pueden presentarse en persona con el menor?', 'Can both parents or guardians appear in person with the child?', 'Eske tou de paran oswa gadyen yo ka prezante an pèsòn avèk timoun nan?', 'Cả hai phụ huynh hoặc người giám hộ có thể đến trực tiếp cùng trẻ không?'),
    opts: yn('END', 'consent2')
  },
  consent2: {
    q: l('¿El otro padre/madre o tutor puede firmar un consentimiento por escrito (ante notario)?', 'Can the other parent or guardian sign a written consent (notarized)?', 'Eske lòt paran oswa gadyen an ka siyen yon konsantman alekri (devan notè)?', 'Phụ huynh hoặc người giám hộ còn lại có thể ký giấy đồng ý bằng văn bản (có công chứng) không?'),
    opts: yn('ADD_3053', 'ADD_5525')
  }
};
const R: Record<string, R> = {
  R_first: {
    forms: ['DS-11'],
    why: l('Por ser su primera vez, se hace una solicitud nueva (DS-11), que se entrega en persona.', 'Since it is your first time, a new application (DS-11) is filed in person.', 'Kòm se premye fwa ou, yo fè yon nouvo demann (DS-11) an pèsòn.', 'Vì đây là lần đầu, quý vị nộp đơn mới (DS-11) trực tiếp.')
  },
  R_minor_renew: {
    forms: ['DS-11'],
    why: l('Los pasaportes de menores generalmente no se renuevan por correo: se hace una solicitud nueva (DS-11) en persona.', 'A minor’s passport generally cannot be renewed by mail: a new application (DS-11) is filed in person.', 'Anjeneral yo pa renouvle paspò timoun pa lapòs: yo fè yon nouvo demann (DS-11) an pèsòn.', 'Hộ chiếu của trẻ em thường không gia hạn qua bưu điện: nộp đơn mới (DS-11) trực tiếp.')
  },
  R_ds82: {
    forms: ['DS-82'],
    why: l('Cumple los requisitos para renovar por correo con el DS-82.', 'You meet the requirements to renew by mail with the DS-82.', 'Ou satisfè kondisyon yo pou renouvle pa lapòs avèk DS-82.', 'Quý vị đủ điều kiện gia hạn qua bưu điện bằng DS-82.')
  },
  R_ds11_old: {
    forms: ['DS-11'],
    why: l('Como se emitió hace 15 años o más, no se puede renovar por correo: se necesita una solicitud nueva (DS-11).', 'Because it was issued 15 or more years ago, it cannot be renewed by mail: a new application (DS-11) is needed.', 'Paske li te emèt sa gen 15 an oswa plis, yo pa ka renouvle l pa lapòs: bezwen yon nouvo demann (DS-11).', 'Vì được cấp từ 15 năm trở lên, không thể gia hạn qua bưu điện: cần đơn mới (DS-11).')
  },
  R_ds11_young: {
    forms: ['DS-11'],
    why: l('Si se emitió antes de los 16 años, no se puede renovar por correo: se necesita una solicitud nueva (DS-11).', 'If it was issued before age 16, it cannot be renewed by mail: a new application (DS-11) is needed.', 'Si li te emèt anvan 16 an, yo pa ka renouvle l pa lapòs: bezwen yon nouvo demann (DS-11).', 'Nếu được cấp trước 16 tuổi, không thể gia hạn qua bưu điện: cần đơn mới (DS-11).')
  },
  R_ds11_name: {
    forms: ['DS-11'],
    why: l('Sin el documento legal del cambio de nombre no se puede renovar por correo: se necesita una solicitud nueva (DS-11).', 'Without the legal document for the name change it cannot be renewed by mail: a new application (DS-11) is needed.', 'San dokiman legal chanjman non an, yo pa ka renouvle l pa lapòs: bezwen yon nouvo demann (DS-11).', 'Không có giấy tờ pháp lý đổi tên thì không thể gia hạn qua bưu điện: cần đơn mới (DS-11).')
  },
  R_lost: {
    forms: ['DS-64', 'DS-11'],
    why: l('Un pasaporte perdido o robado se reporta con el DS-64 y se solicita uno nuevo con el DS-11, en persona.', 'A lost or stolen passport is reported with the DS-64 and a new one is requested with the DS-11, in person.', 'Yon paspò pèdi oswa vòlè rapòte avèk DS-64 epi yo mande yon nouvo avèk DS-11, an pèsòn.', 'Hộ chiếu bị mất hoặc bị đánh cắp được báo cáo bằng DS-64 và xin cấp mới bằng DS-11, trực tiếp.')
  },
  R_lost_1y: {
    forms: ['DS-64', 'DS-5504'],
    why: l('Como se emitió hace menos de 1 año, el reemplazo normalmente se pide con el DS-5504 junto con el DS-64. Lo confirmaremos con usted.', 'Since it was issued less than 1 year ago, the replacement is usually requested with the DS-5504 together with the DS-64. We will confirm with you.', 'Paske li te emèt sa gen mwens pase 1 an, anjeneral yo mande ranplasman an avèk DS-5504 ansanm avèk DS-64. Nou ap konfime avèk ou.', 'Vì được cấp dưới 1 năm, việc cấp thay thường dùng DS-5504 cùng với DS-64. Chúng tôi sẽ xác nhận với quý vị.')
  },
  R_dmg: {
    forms: ['DS-11'],
    why: l('Un pasaporte dañado más allá del desgaste normal no se renueva por correo: se necesita una solicitud nueva (DS-11).', 'A passport damaged beyond normal wear cannot be renewed by mail: a new application (DS-11) is needed.', 'Yon paspò ki domaje plis pase iz nòmal pa ka renouvle pa lapòs: bezwen yon nouvo demann (DS-11).', 'Hộ chiếu hư hỏng quá mức hao mòn bình thường không thể gia hạn qua bưu điện: cần đơn mới (DS-11).')
  },
  R_dmg_1y: {
    forms: ['DS-5504'],
    why: l('Como se emitió hace menos de 1 año, normalmente se reemplaza con el DS-5504. Lo confirmaremos con usted.', 'Since it was issued less than 1 year ago, it is usually replaced with the DS-5504. We will confirm with you.', 'Paske li te emèt sa gen mwens pase 1 an, anjeneral yo ranplase l avèk DS-5504. Nou ap konfime avèk ou.', 'Vì được cấp dưới 1 năm, thường được cấp thay bằng DS-5504. Chúng tôi sẽ xác nhận với quý vị.')
  },
  R_5504_err: {
    forms: ['DS-5504'],
    why: l('Un error de impresión del Departamento de Estado se corrige con el DS-5504.', 'A State Department printing error is corrected with the DS-5504.', 'Yon erè enprimri Depatman Deta a korije avèk DS-5504.', 'Lỗi in của Bộ Ngoại giao được sửa bằng DS-5504.')
  },
  R_5504_chg: {
    forms: ['DS-5504'],
    why: l('Un cambio de nombre o dato dentro del primer año se hace con el DS-5504, por correo.', 'A name or data change within the first year is done with the DS-5504, by mail.', 'Yon chanjman non oswa done nan premye ane a fèt avèk DS-5504, pa lapòs.', 'Đổi tên hoặc thông tin trong năm đầu được thực hiện bằng DS-5504, qua bưu điện.')
  },
  R_notrec: {
    forms: ['DS-86'],
    why: l('Si solicitó un pasaporte y no lo recibió, se presenta la declaración de no recibido (DS-86).', 'If you applied for a passport and did not receive it, you file the statement of non-receipt (DS-86).', 'Si ou te mande yon paspò epi ou pa resevwa l, ou prezante deklarasyon pa resevwa a (DS-86).', 'Nếu đã nộp đơn mà không nhận được hộ chiếu, quý vị nộp bản khai chưa nhận (DS-86).')
  },
  R_unsure: {
    forms: [],
    why: l('No hay problema: cuéntenos su caso y nosotros decidimos juntos cuál es el formulario correcto.', 'No problem: tell us your case and we will work out the right form together.', 'Pa gen pwoblèm: di nou ka ou epi nou ap deside ansanm ki fòm ki bon an.', 'Không sao: hãy cho chúng tôi biết trường hợp của quý vị và chúng tôi sẽ cùng xác định đúng mẫu đơn.')
  }
};
export const FORM_NAME: Record<string, L> = {
  'DS-11': l('Solicitud de pasaporte (nueva)', 'Passport application (new)', 'Demann paspò (nouvo)', 'Đơn xin hộ chiếu (mới)'),
  'DS-82': l('Renovación por correo', 'Renewal by mail', 'Renouvèlman pa lapòs', 'Gia hạn qua bưu điện'),
  'DS-5504': l('Cambio/corrección o reemplazo (dentro de 1 año)', 'Change/correction or replacement (within 1 year)', 'Chanjman/koreksyon oswa ranplasman (nan 1 an)', 'Đổi/sửa hoặc cấp thay (trong 1 năm)'),
  'DS-64': l('Declaración de pasaporte perdido o robado', 'Statement of lost or stolen passport', 'Deklarasyon paspò pèdi oswa vòlè', 'Khai báo hộ chiếu bị mất hoặc đánh cắp'),
  'DS-3053': l('Consentimiento del padre/madre ausente (menores)', 'Consent of absent parent (minors)', 'Konsantman paran ki absan (timoun)', 'Giấy đồng ý của phụ huynh vắng mặt (trẻ em)'),
  'DS-5525': l('Declaración de circunstancias especiales (menores)', 'Statement of special circumstances (minors)', 'Deklarasyon sikonstans espesyal (timoun)', 'Khai báo hoàn cảnh đặc biệt (trẻ em)'),
  'DS-86': l('Declaración de pasaporte no recibido', 'Statement of passport not received', 'Deklarasyon paspò pa resevwa', 'Khai báo chưa nhận hộ chiếu')
};
const UI = {
  title: l('¿No sabe qué formulario necesita?', 'Not sure which form you need?', 'Ou pa konnen ki fòm ou bezwen?', 'Quý vị chưa biết cần mẫu đơn nào?'),
  intro: l('Responda unas preguntas sencillas y le indicamos cuál es el formulario probable. Después lo confirmamos con usted.', 'Answer a few simple questions and we will point you to the likely form. Then we confirm it with you.', 'Reponn kèk kesyon senp epi nou ap montre w fòm ki pi pwobab la. Apre sa nou konfime avèk ou.', 'Trả lời vài câu hỏi đơn giản và chúng tôi sẽ chỉ ra mẫu đơn có khả năng đúng. Sau đó chúng tôi sẽ xác nhận với quý vị.'),
  back: l('Atrás', 'Back', 'Retounen', 'Quay lại'),
  restart: l('Empezar de nuevo', 'Start over', 'Rekòmanse', 'Bắt đầu lại'),
  resultTitle: l('Formulario(s) probable(s)', 'Likely form(s)', 'Fòm ki pi pwobab', 'Mẫu đơn có khả năng'),
  disclaimer: l('Esta guía es una orientación general, no asesoría legal. Antes de preparar su trámite confirmaremos con usted el formulario correcto.', 'This guide is general orientation, not legal advice. Before preparing your paperwork we will confirm the right form with you.', 'Gid sa a se yon oryantasyon jeneral, pa konsèy legal. Anvan nou prepare dosye ou, nou ap konfime fòm ki bon an avèk ou.', 'Hướng dẫn này chỉ mang tính tham khảo chung, không phải tư vấn pháp lý. Trước khi chuẩn bị hồ sơ, chúng tôi sẽ xác nhận mẫu đơn đúng với quý vị.'),
  cta1: l('Ver precios y empezar', 'See prices and get started', 'Gade pri yo epi kòmanse', 'Xem giá và bắt đầu'),
  cta2: l('Confirmar con nosotros', 'Confirm with us', 'Konfime avèk nou', 'Xác nhận với chúng tôi'),
  mention: l('Al escribirnos, mencione estos formularios.', 'When you contact us, mention these forms.', 'Lè w kontakte nou, mansyone fòm sa yo.', 'Khi liên hệ, hãy nhắc đến các mẫu đơn này.')
};
type FinderProps = {
  // Si se pasa, reemplaza los dos botones finales (precios/contacto) y el texto
  // "al escribirnos, mencione". Se usa en /gracias para abrir el cuestionario.
  renderActions?: (forms: string[]) => React.ReactNode;
  disclaimerOverride?: L;
};
export function FormFinder({
  renderActions,
  disclaimerOverride
}: FinderProps) {
  const {
    language
  } = useLanguage();
  const [node, setNode] = useState('start');
  const [minor, setMinor] = useState(false);
  const [extra, setExtra] = useState<string[]>([]);
  const [consentDone, setConsentDone] = useState(false);
  const [pending, setPending] = useState('');
  const [hist, setHist] = useState<{
    node: string;
    minor: boolean;
    extra: string[];
    consentDone: boolean;
    pending: string;
  }[]>([]);
  const go = (next: string, nm: boolean) => {
    setHist(h => [...h, {
      node,
      minor,
      extra,
      consentDone,
      pending
    }]);
    setMinor(nm);
    if (next.startsWith('R_') && nm && R[next].forms.includes('DS-11') && !consentDone) {
      setPending(next);
      setNode('consent');
      return;
    }
    if (next === 'END') {
      setConsentDone(true);
      setNode(pending);
      return;
    }
    if (next === 'ADD_3053' || next === 'ADD_5525') {
      setExtra([next === 'ADD_3053' ? 'DS-3053' : 'DS-5525']);
      setConsentDone(true);
      setNode(pending);
      return;
    }
    setNode(next);
  };
  const back = () => {
    const h = hist[hist.length - 1];
    if (!h) return;
    setHist(hist.slice(0, -1));
    setNode(h.node);
    setMinor(h.minor);
    setExtra(h.extra);
    setConsentDone(h.consentDone);
    setPending(h.pending);
  };
  const restart = () => {
    setHist([]);
    setNode('start');
    setMinor(false);
    setExtra([]);
    setConsentDone(false);
    setPending('');
  };
  const q = QUESTIONS[node];
  const res = node.startsWith('R_') ? R[node] : null;
  const forms = res ? [...res.forms, ...extra.filter(f => !res.forms.includes(f))] : [];
  return <div data-id="element-553" data-mp="2xgye3fqjk5wb">
      {q && <div className="bg-white border border-brand-border rounded-2xl p-5 sm:p-6 shadow-sm" data-id="element-554" data-mp="2xkcbe9kcloww">
          <h2 className="text-xl font-semibold text-brand-textPrimary mb-4" data-id="element-555" data-mp="2joqrggsz1vbc">{q.q[language]}</h2>
          <div className="flex flex-col gap-3" data-id="element-556" data-mp="2ontmr9j5gn64">
            {q.opts.map((o, i) => <button key={i} type="button" onClick={() => {
          const nm = o.minor !== undefined ? o.minor : minor;
          go(nm && o.nextMinor ? o.nextMinor : o.next, nm);
        }} className="text-left px-4 py-4 rounded-xl border border-brand-border bg-brand-bg hover:bg-white hover:border-brand-textPrimary font-medium text-brand-textPrimary focus:outline-none focus:ring-2 focus:ring-brand-accent focus:ring-offset-2" data-id="element-557" data-mp="3cdxnn94ye8tf" data-mpi={i}>
                {o.label[language]}
              </button>)}
          </div>
        </div>}

      {res && <div className="bg-white border border-brand-border rounded-2xl p-5 sm:p-6 shadow-sm" data-id="element-558" data-mp="1v3h87kcqjzkb">
          {forms.length > 0 && <h2 className="text-xl font-semibold text-brand-textPrimary mb-3" data-id="element-559" data-mp="1q3cqjr03cysi">{UI.resultTitle[language]}</h2>}
          {forms.length > 0 && <ul className="flex flex-col gap-2 mb-4" data-id="element-560" data-mp="1m5ezeimrg2mh">
              {forms.map(f => <li key={f} className="flex items-start gap-3" data-id="element-561" data-mp="enecf8q23mkf">
                  <CheckCircle2 className="w-5 h-5 mt-1 text-brand-accent shrink-0" data-id="element-562" data-mp="an47lze03275" />
                  <span className="text-brand-textPrimary" data-id="element-563" data-mp="2mc227zzraeqw"><strong data-id="element-564" data-mp="2m96162sm39y8">{f}</strong> — {FORM_NAME[f][language]}</span>
                </li>)}
            </ul>}
          <p className="text-brand-textSecondary mb-3" data-id="element-565" data-mp="2vc76w2dkez1d">{res.why[language]}</p>
          {!renderActions && forms.length > 0 && <p className="text-sm text-brand-textSecondary mb-3" data-id="element-566" data-mp="302xapb6cetty">{UI.mention[language]}</p>}
          <p className="text-sm text-brand-textSecondary mb-5" data-id="element-567" data-mp="3529zw2kosczp">{(disclaimerOverride || UI.disclaimer)[language]}</p>
          {renderActions ? renderActions(forms) : <div className="flex flex-col sm:flex-row gap-3" data-id="element-568" data-mp="3e1x1ophbvkta">
              <Link to="/pricing" className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-accent hover:bg-brand-accentHover text-brand-textPrimary px-6 py-3 rounded-full font-semibold" data-id="element-569" data-mp="3e0c3mpvt1jqo">
                {UI.cta1[language]} <ArrowRight className="w-4 h-4" data-id="element-570" data-mp="1on8pao8qm2v9" />
              </Link>
              <Link to="/contact" className="flex-1 inline-flex items-center justify-center gap-2 bg-white border border-brand-border text-brand-textPrimary px-6 py-3 rounded-full font-medium" data-id="element-571" data-mp="1opl4wur0w155">
                {UI.cta2[language]}
              </Link>
            </div>}
        </div>}

      {hist.length > 0 && <div className="mt-5 flex items-center gap-4" data-id="element-572" data-mp="1n5ejs4od0dkp">
          <button type="button" onClick={back} className="inline-flex items-center gap-1 text-brand-textSecondary hover:text-brand-textPrimary underline" data-id="element-573" data-mp="1n6zdjs2unshi">
            <ArrowLeft className="w-4 h-4" data-id="element-574" data-mp="1w5tflxyy9b2g" /> {UI.back[language]}
          </button>
          <button type="button" onClick={restart} className="text-brand-textSecondary hover:text-brand-textPrimary underline" data-id="element-575" data-mp="215pa96lrbq5c">
            {UI.restart[language]}
          </button>
        </div>}
    </div>;
}
export function GuiaPage() {
  const {
    language
  } = useLanguage();
  return <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12 sm:py-16" data-id="element-576" data-mp="bqhucto9jmct">
      <h1 className="text-3xl sm:text-4xl font-bold text-brand-textPrimary mb-3" data-id="element-577" data-mp="bqrisctntlge">{UI.title[language]}</h1>
      <p className="text-lg text-brand-textSecondary mb-8" data-id="element-578" data-mp="2p2vnf0k7tylk">{UI.intro[language]}</p>
      <FormFinder data-id="element-579" data-mp="2k8hrmx1n5pgd" />
    </div>;
}